import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import LandingPageHeader from '../../components/LandingPageHeader/LandingPageHeader';
import successImg from '../../resources/images/successIcon.svg';
import BodyText from '../../components/BodyText';
import TextInput from '../../components/TextInput';
import { RedButton, WhiteButton } from '../../components/Button';
import { PaymentInfo } from '../../interfaces/PaymentInfo';
import { formatCurrencyNumber } from '../../utils/amountFormat';
import useSendEmail from '../../hooks/useSendEmail';
import { Pages } from '../../App';
import { ConfigContext } from '../../providers/ConfigContext';
import { updateConfirmationPageData } from '../../DumbleData';
import parseQueryParams from '../../utils/parseQueryParams';
import { Id } from 'react-toastify';
import { SaleResponseInfo } from '../../interfaces/sale-response-info';
import BigNumber from 'bignumber.js';

const tenderTypeMap = new Map([
	['sd', 'Scholastic Dollars'],
	['po', 'Purchase Order'],
	['check', 'Check'],
	['cash', 'Cash'],
	['credit', 'Credit Card'],
	['stf', 'Share the Fair'],
	['ewallet', 'eWallet'],
	['SD', 'Scholastic Dollars'],
	['PO', 'Purchase Order'],
	['CHECK', 'Check'],
	['CASH', 'Cash'],
	['CREDIT', 'Credit Card'],
	['STF', 'Share the Fair'],
	['EWALLET', 'eWallet'],
]);

interface Props {
	paymentInfo: PaymentInfo;
	resetInfo: any;
	defaultConfirmationEmail: string;
	transactionId: string;
	version: string;
	refundAmount: BigNumber;
	payoffAmount: BigNumber;
	saleResponseInfo: SaleResponseInfo;
	cashAmountPaid: BigNumber;
	setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
	success: () => Id;
}

const PaymentConfirmationContent = ({
	paymentInfo,
	resetInfo,
	defaultConfirmationEmail,
	transactionId,
	version,
	refundAmount,
	payoffAmount,
	saleResponseInfo,
	cashAmountPaid,
	setCurrentPage,
	success,
}: Props) => {
	const { bookfairs } = useContext(ConfigContext);

	const [email, setEmail] = React.useState<string>(defaultConfirmationEmail || '');
	const [emailError, setEmailError] = React.useState(false);
	const [emailErrorType, setEmailErrorType] = React.useState('invalidEmail');
	const [sendEmail] = useSendEmail();
	// Following used when processing a request
	const [isButtonProcessing, setIsButtonProcessing] = useState(false);

	const validError = 'Invalid: Please enter a valid value.';
	const apiError = 'Error: Emailed receipt did not send.  Please try again.';

	// DumbleData
	if (version !== 'payoff' || !refundAmount.isZero()) {
		Object.entries(saleResponseInfo.tenders).forEach(([key, value]) => updateConfirmationPageData(tenderTypeMap.get(key) || key, value));
	}

	const isValidEmail = (email: string): boolean => {
		const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		return re.test(String(email).toLowerCase());
	};

	const handleSendReceipt = async () => {
		if (email === '' || !isValidEmail(email)) {
			setEmailError(true);
			setEmailErrorType('invalidEmail');
		} else {
			setIsButtonProcessing(true);
			setEmailError(false);
			const payload = {
				email: email,
			};
			const { fairId } = parseQueryParams(window.location);
			const { data, error } = await sendEmail(transactionId, payload, version === 'payoff' ? fairId : null);
			if (error) {
				setIsButtonProcessing(false);
				setEmailError(true);
				setEmailErrorType('apiError');
			} else if (data != null) {
				resetInfo();
				if (version === 'sale') {
					window._satellite.track('credit-card-send-receipt');
					setCurrentPage(Pages.NewPurchase);
					success();
				} else if (version === 'payoff') {
					window.location.href = `${bookfairs}/content/fairs/hub/home.html`;
				}
			}
		}
	};

	const handleRedirect = () => {
		resetInfo();
		window._satellite.track('credit-card-no-receipt');
		if (version === 'sale') {
			setCurrentPage(Pages.NewPurchase);
		} else if (version === 'payoff') {
			window.location.href = `${bookfairs}/content/fairs/hub/home.html`;
		}
	};

	const renderPaymentDetails = () => {
		const tenders = saleResponseInfo.tenders;
		const isCashOverPayment = tenders.cash === saleResponseInfo.amount && cashAmountPaid.gt(paymentInfo.amountDue);
		const isEWalletRefund = refundAmount.gt(0);
		const isPayoff = version === 'payoff' && payoffAmount.gt(0);

		if (isCashOverPayment && !cashAmountPaid.isZero()) {
			return getCalulationDivRow('Change', cashAmountPaid.minus(paymentInfo.amountDue).toNumber(), false);
		}
		if (isEWalletRefund) {
			return (
				<>
					{getCalulationDivRow('eWallet Refund', refundAmount.toNumber(), true)}
					{getCalulationDivRow('Total', refundAmount.toNumber(), false)}
				</>
			);
		}
		if (isPayoff) {
			return (
				<>
					{getCalulationDivRow('Payoff', payoffAmount.toNumber(), true)}
					{getCalulationDivRow('Total', payoffAmount.toNumber(), false)}
				</>
			);
		}

		return (
			<>
				{Object.entries(tenders).map(([key, value]) => (
					<React.Fragment key={key}>{getCalulationDivRow(tenderTypeMap.get(key) || key, value, true)}</React.Fragment>
				))}
				{getCalulationDivRow('Total', saleResponseInfo.amount, false)}
			</>
		);
	};

	const getCalulationDivRow = (label: string, value: number, withBreak: boolean) => {
		return (
			<>
				<RowItemDiv>
					<ItemLabel>{label}</ItemLabel>
					<ItemValue>{formatCurrencyNumber(value)}</ItemValue>
				</RowItemDiv>
				{withBreak && <HorizontalLine />}
			</>
		);
	};

	return (
		<>
			<LandingPageHeader />
			<PaymentConfirmationPage>
				<ContentWrapper>
					<StyledImg src={successImg} alt="success" />
					<HeaderText>Transaction Successful</HeaderText>
					{saleResponseInfo.maskedCard && (
						<CardSummaryText>
							{saleResponseInfo.cardType} {saleResponseInfo.maskedCard}
						</CardSummaryText>
					)}
					<CalculationsDiv>{renderPaymentDetails()}</CalculationsDiv>
					<TextInput
						label="Email for Receipt"
						placeholder="Enter email address"
						value={email}
						onChange={setEmail}
						error={emailError}
						errorText={emailErrorType === 'invalidEmail' ? validError : apiError}
					/>
					<StyledRedButton disabled={email === '' || isButtonProcessing} handleClick={handleSendReceipt}>
						Send Receipt
					</StyledRedButton>
					<HorizontalLine />
					<StyledWhiteButton whiteBackground={true} handleClick={handleRedirect}>
						No Receipt
					</StyledWhiteButton>
				</ContentWrapper>
			</PaymentConfirmationPage>
		</>
	);
};

const PaymentConfirmationPage = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
`;

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 280px;
`;

const StyledImg = styled.img`
	width: 48px;
	height: 48px;
	flex-shrink: 0;
	margin-top: 40px;
`;

const HeaderText = styled(BodyText)`
	font-size: 24px;
	font-style: normal;
	font-weight: 400;
	line-height: 28px;
	text-align: center;
	margin: 15px 0 34px 0;
`;

const CardSummaryText = styled(BodyText)`
	font-size: 13px;
	font-weight: 400;
	line-height: 15px;
	text-align: center;
	margin: 0 0 12px 0;
`;

const CalculationsDiv = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;
	margin-bottom: 38px;
`;

const RowItemDiv = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
`;

const ItemLabel = styled(BodyText)`
	font-size: 16px;
	font-weight: 500;
	line-height: 19px;
	text-align: left;
	margin: 0;
`;

const ItemValue = styled(BodyText)`
	font-size: 21px;
	font-weight: 500;
	line-height: 24px;
	text-align: right;
	margin: 0;
`;

const HorizontalLine = styled.div`
	width: 100%;
	height: 1px;
	background: #d7d7d7;
	margin: 0;
`;

const StyledRedButton = styled(RedButton)`
	width: 100%;
	margin: 32px 0 32px 0;
`;

const StyledWhiteButton = styled(WhiteButton)`
	width: 100%;
	height: 40px;
	padding: 0;
	margin: 32px 0 32px 0;
`;

export default PaymentConfirmationContent;
